import { useState } from 'react'

interface NotesTabProps {
  jobData: any
  setJobData: (data: any) => void
  readOnly?: boolean
}

interface Note {
  id?: string
  note_type: 'FOLLOWUP' | 'INTERNAL' | 'ACTION'
  content: string
  due_date?: string
  is_completed: boolean
}

export default function NotesTab({ jobData, setJobData, readOnly = false }: NotesTabProps) {
  const [newNote, setNewNote] = useState<Note>({
    note_type: 'INTERNAL',
    content: '',
    is_completed: false
  })

  const notes = jobData.notes || []

  const addNote = () => {
    if (!newNote.content.trim()) {
      alert('Please enter a note')
      return
    }

    setJobData({
      ...jobData,
      notes: [...notes, { ...newNote, id: Date.now().toString() }]
    })

    setNewNote({
      note_type: 'INTERNAL',
      content: '',
      is_completed: false
    })
  }

  const removeNote = (index: number) => {
    setJobData({
      ...jobData,
      notes: notes.filter((_: any, i: number) => i !== index)
    })
  }

  const updateNote = (index: number, field: string, value: any) => {
    const updated = [...notes]
    updated[index] = { ...updated[index], [field]: value }
    setJobData({ ...jobData, notes: updated })
  }

  return (
    <div className="space-y-6">
      {/* Notes List */}
      <div>
        <h3 className="font-medium text-gray-900 dark:text-white mb-4">Notes</h3>
        {notes.length > 0 && (
          <div className="space-y-3">
            {notes.map((note: any, idx: number) => (
              <div key={idx} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700/50">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      note.note_type === 'ACTION'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        : note.note_type === 'FOLLOWUP'
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                        : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                    }`}>
                      {note.note_type}
                    </span>
                    {note.due_date && (
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        Due: {new Date(note.due_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => removeNote(idx)}
                    className="text-red-600 hover:text-red-900 dark:hover:text-red-400"
                  >
                    Delete
                  </button>
                </div>
                <p className="text-gray-700 dark:text-gray-300 mb-3">{note.content}</p>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={note.is_completed}
                    onChange={(e) => updateNote(idx, 'is_completed', e.target.checked)}
                    disabled={readOnly}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <label className="text-sm text-gray-600 dark:text-gray-400">
                    {note.is_completed ? 'Completed' : 'Mark as completed'}
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add New Note */}
      <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg space-y-4">
        <h3 className="font-medium text-gray-900 dark:text-white">Add Note</h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Note Type <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-3 gap-3">
            {[
              { value: 'INTERNAL' as const, label: 'Internal' },
              { value: 'FOLLOWUP' as const, label: 'Follow-up' },
              { value: 'ACTION' as const, label: 'Action Required' }
            ].map((type) => (
              <button
                key={type.value}
                onClick={() => setNewNote({ ...newNote, note_type: type.value })}
                className={`px-3 py-2 rounded-md font-medium transition ${
                  newNote.note_type === type.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Note Content <span className="text-red-500">*</span>
          </label>
          <textarea
            value={newNote.content}
            onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
            disabled={readOnly}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
            rows={3}
            placeholder="Enter your note here..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Due Date
          </label>
          <input
            type="date"
            value={newNote.due_date || ''}
            onChange={(e) => setNewNote({ ...newNote, due_date: e.target.value || undefined })}
            disabled={readOnly}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
          />
        </div>

        <button
          onClick={addNote}
          disabled={readOnly}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          Add Note
        </button>
      </div>
    </div>
  )
}
